package com.siriusxm.pia.player

import com.siriusxm.cmc.utils.ImageFormat
import com.siriusxm.cmc.utils.ResizeParams
import com.siriusxm.pia.Application
import com.siriusxm.pia.rest.unifiedaggregator.ImageRenderingHelper
import kotlinx.browser.window

/** Legacy HTTP image hosts proxied over HTTPS through the CMC CloudFront `/legacy-images/{prefix}` behavior. */
private val LEGACY_IMAGE_HOSTS = mapOf(
    "pri.art.prod.streaming.siriusxm.com" to "prod",
    "pri.art.uat.streaming.siriusxm.com" to "uat",
)

/**
 * Turn the server-selected artwork [path] into a servable CDN URL for the player. The host injects this
 * into every [SdkCMCPlayer] it creates.
 *
 * The server *selects* the best 1:1 tile but returns its raw aggregator storage key (e.g.
 * `podcast/e340…`), which is not directly loadable; building the CDN URL is the frontend's job because
 * the atlas base URL is per-env `config.json` config the service never sees. This mirrors the guarded,
 * idempotent transform the aggregator views use ([ImageViewers.kt]): an absolute `http(s)` URL is left
 * alone (a known legacy HTTP host is rewritten through the HTTPS proxy to avoid mixed-content errors),
 * and only a relative storage key is base64-wrapped through the atlas resize endpoint — wrapping an
 * already-absolute URL would corrupt it. Resized to `112×112` (2× the 56 px bar tile, for retina).
 * Null/blank yields null so the bar shows its placeholder tile.
 */
internal fun playerArtworkUrl(path: String?): String? {
    val raw = path?.takeIf { it.isNotBlank() } ?: return null
    if (raw.startsWith("http://")) {
        val rest = raw.removePrefix("http://")
        val slashIdx = rest.indexOf('/')
        val host = if (slashIdx == -1) rest else rest.substring(0, slashIdx)
        val suffix = if (slashIdx == -1) "" else rest.substring(slashIdx)
        val prefix = LEGACY_IMAGE_HOSTS[host.lowercase()] ?: return raw
        return "${window.location.origin}/legacy-images/$prefix$suffix"
    }
    if (raw.startsWith("https://")) return raw
    return ImageRenderingHelper.getImageUrl(
        Application.configuration.atlasImageServerBaseUrl,
        raw,
        ResizeParams(112, 112),
        ImageFormat.JPEG,
    )
}
